import { indefiniteArticle } from '@packages/format';
import { EntityTypeSchema, FIRM_ENTITY_TYPES, isEntityType } from './api/account.js';
import { registrationNumberKind, registrationDateMeaning } from './entity-identifier.js';
const ENTITY_TYPE_LABELS = {
    INDIVIDUAL: 'Individual',
    PTY_LTD: 'Private Company [(Pty) Ltd]',
    INC: 'Personal Liability Company (Inc)',
    CC: 'Close Corporation (CC)',
    PARTNERSHIP: 'Partnership',
    TRUST: 'Trust',
    FOREIGN_COMPANY: 'Foreign Company',
    BODY_CORPORATE: 'Body Corporate',
    NPC: 'Non-Profit Company (NPC)',
    VOLUNTARY_ASSOCIATION: 'Voluntary Association',
    DECEASED_ESTATE: 'Deceased Estate',
};
export function formatEntityType(entityType) {
    return ENTITY_TYPE_LABELS[entityType];
}
/**
 * `formatEntityType`, prefixed with the article its label actually needs
 * ("an Individual", "a Trust"). Derived from the label's own first letter
 * rather than hardcoding "Individual" as the one vowel-initial case, so a
 * future label starting with a vowel sound gets the right article without
 * this function needing to change.
 */
export function formatEntityTypeWithArticle(entityType) {
    const label = formatEntityType(entityType);
    return `${indefiniteArticle(label)} ${label}`;
}
const LEGAL_NAME_FIELD_BY_ENTITY_TYPE = {
    INDIVIDUAL: { label: 'Full name', placeholder: 'Thabo Nkosi' },
    PARTNERSHIP: { label: 'Legal name', placeholder: 'Naidoo & Partners' },
    PTY_LTD: { label: 'Legal name', placeholder: 'Khumalo Trading (Pty) Ltd' },
    CC: { label: 'Legal name', placeholder: 'De Klerk Consulting CC' },
    INC: { label: 'Legal name', placeholder: 'Adams Professional Services Inc.' },
    TRUST: { label: 'Legal name', placeholder: 'Mthembu Family Trust' },
    FOREIGN_COMPANY: { label: 'Legal name', placeholder: 'Acme International Ltd' },
    BODY_CORPORATE: { label: 'Legal name', placeholder: 'Body Corporate of SS Sunrise 123' },
    NPC: { label: 'Legal name', placeholder: 'Sunridge Homeowners NPC' },
    VOLUNTARY_ASSOCIATION: { label: 'Legal name', placeholder: 'Sunridge Homeowners Association' },
    DECEASED_ESTATE: { label: 'Legal name', placeholder: 'Estate Late Mokgadi Kgosi' },
};
const DEFAULT_LEGAL_NAME_FIELD = { label: 'Legal name', placeholder: '' };
/**
 * The label and SA-plausible placeholder for a client's legal-name input,
 * keyed off its entity type. Three branches, not two: a not-recorded entity
 * type (`null` / `undefined` / `''`) and an unrecognised string both fall
 * through to the generic default - the map is exhaustive over `EntityType`
 * itself, so "unmatched after the guard" can only mean a string this bundle
 * does not know.
 */
export function getLegalNameField(entityType) {
    if (!entityType)
        return DEFAULT_LEGAL_NAME_FIELD;
    if (!isEntityType(entityType))
        return DEFAULT_LEGAL_NAME_FIELD;
    return LEGAL_NAME_FIELD_BY_ENTITY_TYPE[entityType];
}
/**
 * The label for each `RegistrationNumberKind` - what a value stored in the
 * `registrationNumber` column IS, independent of whether Bill's own forms
 * offer a field for it. `NONE` shares the generic default with an
 * unrecorded/unknown entity type because there is nothing more specific to
 * call it.
 */
const REGISTRATION_NUMBER_LABEL_BY_KIND = {
    CIPC: 'Registration number',
    TRUST_REFERENCE: 'Trust reference number',
    SCHEME_NUMBER: 'Scheme number',
    MASTERS_REFERENCE: "Master's reference number",
    ID_NUMBER: 'ID number',
    NONE: 'Registration number',
};
/**
 * SA-plausible placeholder for a kind whose number format does not vary by
 * entity type. `CIPC` is the exception - the registration-number suffix
 * differs by entity type (Pty Ltd `/07`, CC `/23`, ...), so its placeholders
 * stay keyed by `EntityType` below; that is an example-string distinction,
 * not a difference in meaning, which is why it does not appear as a
 * `RegistrationNumberKind`.
 */
const REGISTRATION_NUMBER_PLACEHOLDER_BY_KIND = {
    TRUST_REFERENCE: 'IT1234/2023(C)',
    SCHEME_NUMBER: 'SS 123/2005',
    MASTERS_REFERENCE: '12345/2026',
};
const CIPC_PLACEHOLDER_BY_ENTITY_TYPE = {
    PTY_LTD: '2023/123456/07',
    CC: '2023/123456/23',
    INC: '2023/123456/21',
    FOREIGN_COMPANY: '2023/123456/10',
    NPC: '2023/123456/08',
};
const DEFAULT_REGISTRATION_NUMBER_FIELD = { label: 'Registration number', placeholder: '' };
export function getRegistrationNumberField(entityType) {
    // An entity type that was never recorded says nothing about which identifier the entity holds,
    // so offering a generic "Registration number" invents one. Any number already stored is kept
    // and shown again once a type is set.
    if (!entityType)
        return null;
    // An unrecognised string keeps the pre-widening default: the map is exhaustive over
    // EntityType, so "unmatched after the not-recorded guard" can only mean a string this
    // bundle does not know, not a value that should have no field.
    if (!isEntityType(entityType))
        return DEFAULT_REGISTRATION_NUMBER_FIELD;
    const kind = registrationNumberKind(entityType);
    // An individual's ID number (ID_NUMBER) and the absence of any identifier for a partnership
    // or voluntary association (NONE) are both stored facts, not gaps - see `RegistrationNumberKind`.
    // Neither is offered as a field on Bill's own onboarding/detail forms: an individual's ID number
    // is captured elsewhere, and an unregistered entity has nothing to enter.
    if (kind === 'ID_NUMBER' || kind === 'NONE')
        return null;
    const placeholder = kind === 'CIPC' ? (CIPC_PLACEHOLDER_BY_ENTITY_TYPE[entityType] ?? '') : (REGISTRATION_NUMBER_PLACEHOLDER_BY_KIND[kind] ?? '');
    return { label: REGISTRATION_NUMBER_LABEL_BY_KIND[kind], placeholder };
}
/**
 * The label for whatever a stored `registrationNumber` value IS, regardless
 * of whether `getRegistrationNumberField` offers a form field for it -
 * `INDIVIDUAL` returns `'ID number'` even though the field itself is `null`.
 */
export function getRegistrationNumberLabel(entityType) {
    if (!entityType)
        return REGISTRATION_NUMBER_LABEL_BY_KIND.NONE;
    if (!isEntityType(entityType))
        return REGISTRATION_NUMBER_LABEL_BY_KIND.NONE;
    return REGISTRATION_NUMBER_LABEL_BY_KIND[registrationNumberKind(entityType)];
}
const REGISTRATION_DATE_FIELD_BY_MEANING = {
    DATE_OF_BIRTH: { label: 'Date of birth', placeholder: '1984-11-03' },
    DATE_OF_DEATH: { label: 'Date of death', placeholder: '2026-02-10' },
    REGISTRATION_DATE: { label: 'Registration date', placeholder: '2009-06-15' },
};
const DEFAULT_REGISTRATION_DATE_FIELD = { label: 'Registration date', placeholder: '' };
/**
 * The label and SA-plausible placeholder for the `registrationDate` column,
 * derived from the same `RegistrationNumberKind` as
 * `getRegistrationNumberField`. `null` for `NONE` (a partnership or
 * voluntary association has no such date) and for an unrecorded entity type,
 * for the same reason `getRegistrationNumberField` returns `null` there.
 */
export function getRegistrationDateField(entityType) {
    if (!entityType)
        return null;
    if (!isEntityType(entityType))
        return DEFAULT_REGISTRATION_DATE_FIELD;
    const meaning = registrationDateMeaning(registrationNumberKind(entityType));
    if (meaning === 'NONE')
        return null;
    return REGISTRATION_DATE_FIELD_BY_MEANING[meaning] ?? DEFAULT_REGISTRATION_DATE_FIELD;
}
/**
 * Every entity type, in schema order. The right list for a CLIENT picker and
 * for an administered entity's own books: a client, and an entity a firm
 * administers, can be any of them.
 */
export function getEntityTypeOptions() {
    return EntityTypeSchema.options.map(t => ({ value: t, label: formatEntityType(t) }));
}
/**
 * The entity types a FIRM's own books may carry, in schema order - the list for
 * a picker editing the firm's own business entity.
 *
 * This exists so no call site has to invert `FIRM_ENTITY_TYPES` back into an
 * exclusion. It is deliberately not paired with an
 * `getAdministeredEntityTypeOptions`, because a firm's entity types and an
 * administered entity's are not a partition: an administered entity can be
 * any entity type at all - a company in business rescue is a `PTY_LTD`, an
 * insolvent estate a `DECEASED_ESTATE` - so its list is
 * `getEntityTypeOptions()` above, not a complement.
 *
 * `EntityType` and `PracticeType` are different axes and do not line up
 * one-to-one: `DECEASED_ESTATE` is an `EntityType` (a client's legal entity
 * type), while `ESTATE_ADMINISTRATION` is the administered-entity
 * `PracticeType` for a *solvent* deceased estate's books - the entity type
 * says what the client legally is, the practice type says which kind of
 * books the firm keeps for it. An INSOLVENCY entity's chart is further
 * chosen by its entity type (`INSOLVENCY_PRESET_BY_ENTITY_TYPE` in
 * `domains/ledger/chart-of-accounts.presets.insolvency.ts`), but that map
 * refuses a `DECEASED_ESTATE` entity type today: an insolvent deceased
 * estate carries s 96 funeral and death-bed implications nobody has
 * specified yet, so setting up insolvency books for one is declined rather
 * than seeded on a chart that would be wrong. That is a deferral, not a
 * permanent gap - the combination is a real one, just not a supported one.
 */
export function getFirmEntityTypeOptions() {
    return EntityTypeSchema.options
        .filter((t) => FIRM_ENTITY_TYPES.includes(t))
        .map(t => ({ value: t, label: formatEntityType(t) }));
}
